import { reportHandledException } from '@/lib/error-reporting'
import React from 'react'
import Image from 'next/image'

/** Site covers share one crop on every screen; social cards keep their own frame. */
const COVER_RATIO = { width: 16, height: 9 } as const
export const BLOG_COVER_ASPECT_RATIO = `${COVER_RATIO.width} / ${COVER_RATIO.height}`

export function BlogCoverFrame({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      data-blog-cover=""
      className={`relative isolate block self-start overflow-hidden ${className}`}
      style={{ aspectRatio: BLOG_COVER_ASPECT_RATIO }}
    >
      {children}
    </div>
  )
}

/**
 * Everything that decides how wide a blog picture is actually delivered.
 *
 * Articles store ONE object per picture, up to 2400px wide (see
 * `features/blog-admin/editor/image-upload.ts`). The responsive ladder is
 * derived here, at request time, from that single object: covers go through
 * `next/image`, and the body's `<img>` tags — which arrive as an HTML string
 * from the API and never pass through React — get a `srcset` spliced in by
 * {@link withContentImageSrcset}. Nothing is written back to the database:
 * `posts.content_html` must not carry `/_next/image` URLs, which belong to
 * whichever deployment served the page.
 *
 * All three call sites need the same predicate — is this src one of OUR
 * objects — so they share this module rather than each re-deriving the origin.
 */

/**
 * Origin of the bucket blog images live in, or null when this deployment was
 * never told about one.
 *
 * `NEXT_PUBLIC_` because a cover is rendered by a client component: a
 * server-only variable would be `undefined` during hydration and flip the
 * branch below under the reader. The value is not a secret — it is the origin
 * of every image URL already in the page.
 *
 * It must match the API's `R2_BLOG_PUBLIC_BASE_URL`, which is what builds the
 * URLs stored on the post. Unset (or unparseable), every picture keeps the
 * plain `<img>` it has today: the optimizer would refuse an origin missing
 * from `images.remotePatterns` in `next.config.ts`, and a broken picture is a
 * worse answer than an unoptimized one.
 */
export function blogImageOrigin(): string | null {
  const raw = process.env.NEXT_PUBLIC_R2_BLOG_BASE_URL?.trim()
  if (!raw) return null
  try {
    return new URL(raw).origin
  } catch (caughtError) {
    reportHandledException(caughtError, { source: 'features/blog/blog-image.tsx' })
    return null
  }
}

/** Whether `src` is one of this site's own blog objects, and so optimizable. */
export function isBlogImageSrc(src: string): boolean {
  const origin = blogImageOrigin()
  if (origin === null) return false
  try {
    return new URL(src).origin === origin
  } catch (caughtError) {
    reportHandledException(caughtError, { source: 'features/blog/blog-image.tsx' })
    return false
  }
}

/**
 * A single rung of the ladder. `q=75` is the loader's own default; `w` must be
 * one of the widths `next.config.ts` allows, which is why {@link CONTENT_WIDTHS}
 * is a subset of Next's default `deviceSizes` rather than anything derived
 * from the image.
 */
function nextImageSrc(src: string, width: number): string {
  return `/_next/image?url=${encodeURIComponent(src)}&amp;w=${width}&amp;q=75`
}

/**
 * Widths offered for a body picture, narrowed per image to those the stored
 * object can actually fill. Every entry is one of Next's default `deviceSizes`
 * — `/_next/image` answers 400 to a width outside that set.
 */
const CONTENT_WIDTHS = [640, 750, 828, 1080, 1200, 1920] as const

/**
 * The body column, measured rather than guessed: the article fills the grid
 * column beside the table of contents. On the 1060px container that is 732px
 * (less the 32px gutters, the 212px rail and the 52px gap); between 940px and
 * 1060px it is the viewport less those; below 940px the rail drops and the
 * body takes the container; below 620px the gutters are 20px.
 */
const CONTENT_SIZES =
  '(max-width: 620px) calc(100vw - 40px), (max-width: 939px) calc(100vw - 64px), (max-width: 1060px) calc(100vw - 328px), 732px'

interface ParsedImgTag {
  attributes: Map<string, string>
  /** Offset of the `>` (or the `/` of `/>`) that closes the tag. */
  closesAt: number
}

/**
 * Reads one `<img>`'s attributes, starting just past the tag name.
 *
 * Attribute by attribute, never by scanning for the tag's `>`: `alt` is
 * author-written text and Tiptap's serializer escapes only `"` in an attribute
 * value, so a `>` or `<` inside an `alt` reaches the string intact — the exact
 * trap documented at the top of `packages/ui-kit/src/blog-render.ts`. Reading
 * values as `"`-delimited runs steps over that text instead of stopping in the
 * middle of it.
 *
 * Anything that does not match the shape this schema emits (a bare attribute,
 * a single-quoted value, an unterminated tag) yields null and the tag is left
 * exactly as it arrived.
 */
function readImgTag(html: string, from: number): ParsedImgTag | null {
  const attributes = new Map<string, string>()
  let cursor = from
  for (;;) {
    while (cursor < html.length && /\s/.test(html[cursor])) cursor += 1
    if (cursor >= html.length) return null
    if (html[cursor] === '>') return { attributes, closesAt: cursor }
    if (html[cursor] === '/' && html[cursor + 1] === '>') return { attributes, closesAt: cursor }

    const nameStart = cursor
    while (cursor < html.length && /[A-Za-z0-9:_-]/.test(html[cursor])) cursor += 1
    if (cursor === nameStart) return null
    if (html[cursor] !== '=' || html[cursor + 1] !== '"') return null

    const valueStart = cursor + 2
    const valueEnd = html.indexOf('"', valueStart)
    if (valueEnd === -1) return null
    attributes.set(html.slice(nameStart, cursor), html.slice(valueStart, valueEnd))
    cursor = valueEnd + 1
  }
}

/**
 * The `srcset` for one body picture, or null when there is nothing to gain.
 *
 * The stored `width` is the ceiling: offering a rung wider than the object
 * would describe a candidate the optimizer cannot produce, and the browser
 * would pick it. A figure written before `width` existed, a picture already
 * narrower than the smallest rung, and anything hosted somewhere other than
 * our own bucket all keep the single `src` they have.
 */
function contentSrcset(attributes: Map<string, string>): string | null {
  const src = attributes.get('src')
  if (src === undefined || attributes.has('srcset') || !isBlogImageSrc(src)) return null
  const width = Number(attributes.get('width'))
  if (!Number.isFinite(width) || width <= 0) return null
  const widths = CONTENT_WIDTHS.filter((candidate) => candidate <= width)
  if (widths.length === 0) return null
  return widths.map((candidate) => `${nextImageSrc(src, candidate)} ${candidate}w`).join(', ')
}

/**
 * Adds `srcset`/`sizes` to every body `<img>` the optimizer can serve, leaving
 * the rest of the document byte-for-byte as it arrived — including the
 * original `src`, which stays the fallback for a client that ignores `srcset`.
 */
export function withContentImageSrcset(html: string): string {
  const pieces: string[] = []
  let copiedTo = 0
  let search = 0
  for (;;) {
    const tagStart = html.indexOf('<img', search)
    if (tagStart === -1) break
    search = tagStart + 4
    const tag = readImgTag(html, search)
    if (tag === null) continue
    search = tag.closesAt
    const srcset = contentSrcset(tag.attributes)
    if (srcset === null) continue
    pieces.push(html.slice(copiedTo, tag.closesAt), ` srcset="${srcset}" sizes="${CONTENT_SIZES}"`)
    copiedTo = tag.closesAt
  }
  if (pieces.length === 0) return html
  pieces.push(html.slice(copiedTo))
  return pieces.join('')
}

/** Object-cover scales wide sources by height; request that uncropped pixel width. */
export function coverImageSizes(sizes: string, width: number, height: number): string {
  const scale = width / height / (COVER_RATIO.width / COVER_RATIO.height)
  if (!Number.isFinite(scale) || scale <= 1) return sizes
  // A sizes list contains commas inside min()/calc(), so split only at depth zero.
  const slots: string[] = []
  let depth = 0
  let start = 0
  for (let i = 0; i < sizes.length; i++) {
    if (sizes[i] === '(') depth++
    if (sizes[i] === ')') depth--
    if (sizes[i] === ',' && depth === 0) {
      slots.push(sizes.slice(start, i).trim())
      start = i + 1
    }
  }
  slots.push(sizes.slice(start).trim())
  return slots
    .map((slot) => {
      let split = -1
      depth = 0
      for (let i = 0; i < slot.length; i++) {
        if (slot[i] === '(') depth++
        if (slot[i] === ')') depth--
        if (slot[i] === ' ' && depth === 0) split = i
      }
      const condition = split < 0 ? '' : slot.slice(0, split + 1)
      const size = slot.slice(split + 1)
      return `${condition}calc(${size} * ${scale})`
    })
    .join(', ')
}

interface BlogCoverImageProps {
  src: string
  alt: string
  /** Dimensions of the stored master, before the display crop. */
  width: number
  height: number
  /** The layout width of the box this fills, for the browser's rung choice. */
  sizes: string
  /** Set on the one cover that is above the fold — the article's hero. */
  priority?: boolean
}

/**
 * A cover picture filling its (already positioned, already aspect-boxed)
 * parent. `fill` rather than intrinsic `width`/`height` because the box owns
 * the shape and the picture is cropped into it; the surrounding element keeps
 * the border, radius and aspect ratio it had.
 */
export function BlogCoverImage({
  src,
  alt,
  sizes,
  width,
  height,
  priority = false,
}: BlogCoverImageProps) {
  if (!isBlogImageSrc(src)) {
    return (
      <img
        src={src}
        alt={alt}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover object-center"
      />
    )
  }
  return (
    <Image
      src={src}
      alt={alt}
      fill
      sizes={coverImageSizes(sizes, width, height)}
      // `preload`, not `priority`: 16 deprecated the latter. Same effect for
      // this call site — the hero cover gets a `<link rel=preload>` and drops
      // out of lazy loading.
      preload={priority}
      className="object-cover object-center"
    />
  )
}
